import React, {Component, RefObject} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownItemTeamAvatar} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemTeamAvatar';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemChevron} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemChevron';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {TagModel} from '../../../../models/api/tagModel';
import {VisualWorkspaceTag} from '../../../../models/api/visualWorkspaceModel';
import {TagModelMap} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {workspacesEditorDropdownWidth} from '../editor/context/types';
import {TagCreatedHandler, ToggleDropdownTagHandler} from '../editor/context/workspacesEditorContext';
import {WorkspacesEditorPickTagsDropdownContainer} from './workspacesEditorPickTagsDropdownContainer';

/*
 * Constants.
 */

const dropdownBackButtonButtonLabel = (
  <Trans id="workspaces-editor-team-tags-options">Shared tags options</Trans>
);

/*
 * Props.
 */

interface TeammateMoreDropdownProps extends DropdownProps {
  teams: ReadonlyArray<TeamModel>;
  anchor: RefObject<HTMLDivElement>;
  visualWorkspaceTags: ReadonlyArray<VisualWorkspaceTag>;
  onToggleTeamTag: ToggleDropdownTagHandler;
  onRequestClose: () => void;
  tagModelMap: TagModelMap;
  onTagCreated: TagCreatedHandler;
  tagDropdownSelectedIds: ReadonlyArray<TagModel['id']>;
}

/*
 * Component.
 */

interface TeammateMoreDropdownState {
  activeTeam?: TeamModel;
}

/**
 * This component is adapted from the now-removed <TeammateMoreDropdown /> but for use in <WorkspacesEditor />.
 */
export class WorkspacesEditorTeammateMoreDropdown extends Component<
  TeammateMoreDropdownProps,
  TeammateMoreDropdownState
> {
  constructor(props: TeammateMoreDropdownProps) {
    super(props);

    // When there is only one team, show its tags directly. Otherwise, activeTeam is undefined
    // until the user selects one of the teams.
    this.state = props.teams.length === 1 ? {activeTeam: props.teams[0]} : {activeTeam: undefined};
  }

  /*
   * Event handlers.
   */

  private openTeam(e: FrontEvent, team: TeamModel) {
    e.preventDefault();
    this.setState({activeTeam: team});
  }

  private readonly onBackClick = () => {
    this.setState({activeTeam: undefined});
  };

  /*
   * Render.
   */

  render() {
    const {activeTeam} = this.state;
    const {
      anchor,
      onRequestClose,
      visualWorkspaceTags,
      onToggleTeamTag,
      tagModelMap,
      onTagCreated,
      tagDropdownSelectedIds,
    } = this.props;

    // If a team is selected, show its tags.
    if (activeTeam) {
      return (
        <WorkspacesEditorPickTagsDropdownContainer
          team={activeTeam}
          anchor={anchor}
          backButtonLabel={dropdownBackButtonButtonLabel}
          onBackButtonClick={this.onBackClick}
          placement="right-start"
          onRequestClose={onRequestClose}
          width={workspacesEditorDropdownWidth}
          visualWorkspaceTags={visualWorkspaceTags}
          onToggleTag={onToggleTeamTag}
          tagModelMap={tagModelMap}
          onTagCreated={onTagCreated}
          tagDropdownSelectedIds={tagDropdownSelectedIds}
        />
      );
    }

    // Otherwise, show all the team options so the user can choose which team tags to add.
    return (
      <Dropdown {...this.props}>
        <DropdownHeader title={<Trans id="workspaces-editor-team-tags">Shared tags</Trans>} />
        <DropdownList {...this.props}>
          {/* Team tags */}
          {this.props.teams.map((team) => (
            <DropdownItem onSelect={(e) => this.openTeam(e, team)} key={team.id}>
              <DropdownItemTeamAvatar team={team} />
              <DropdownItemContent>
                <Trans id="workspace-teamtags">{team.name} tags</Trans>
              </DropdownItemContent>
              <DropdownItemChevron />
            </DropdownItem>
          ))}
        </DropdownList>
      </Dropdown>
    );
  }
}
